import { yupResolver } from '@hookform/resolvers/yup';
import type { SelectChangeEvent } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

// import Popup1099NEC from '@/components/ui/1099PopupNec';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import CustomTextMask from '@/components/ui/CustomTextMask';
import Loader from '@/components/ui/Loader';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { ContactUsType } from '@/types';
import { PartnerType } from '@/types/enumTypes';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const Partner = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnack, setSnackOpen] = useState(false);
  const [isLabelName, setIsLabelName] = useState('Partner Name');

  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Now Accepting <span style="color:#1da94a; font-weight: 600;">Form 1098-E</span> for Tax Year 2024!`,
  //   SubHeading: 'File your student loan interest deductions with ease.  ',
  //   CtaText: ' Get Started Today!',
  //   CtaSubHeading: ``,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1098e`,
  //   isActive: true,
  // };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string().required('Full Name is required'),
      DesignationOfContactPerson: Yup.string().required(
        'Designation of contact person is required'
      ),
      Email: Yup.string()
        .required('Email Address is required')
        .email('Enter valid Email Address'),
      Phone: Yup.string()
        .required('Phone Number is required')
        .matches(
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
          'Please enter a valid phone number'
        ),
      Message: Yup.string().required('Description/Comments is required'),
    },
    [
      // Add Cyclic deps here because when require itself
      ['Phone', 'Phone'],
    ]
  );

  const { handleSubmit, control, setValue, reset } = useForm<ContactUsType>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      ContactUsType,
      any
    >,
    defaultValues: {
      Name: '',
      Email: '',
      Phone: '',
      Message: '',
      DesignationOfContactPerson: '',
      NatureOfBusiness: '',
      PartnerName: '',
      PartnerType: PartnerType.None,
      Token: '',
    },
  });

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: ContactUsType) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: ContactUsType = data;
          postData.Token = token;

          const address = `/api/contactus/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData?.status === 'Success') {
            reset();
            setValue('PartnerType', PartnerType.None);
            setSnackOpen(true);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
    reset();
  };

  const handlePartnerType = (event: SelectChangeEvent) => {
    setValue('PartnerName', '');
    let string = '';
    switch (event.target.value) {
      case PartnerType.Business:
        string = 'Business name';
        break;
      // case PartnerType.TaxProfessional:
      //   string = 'Freelancer or entity';
      //   break;
      case PartnerType.Influencer:
        string = 'Social media account link for reference';
        break;
      case PartnerType.Association:
        string = 'Association name';
        break;
      // case PartnerType.SoftwareProvider:
      //   string = 'Product name ';
      //   break;
      default:
        string = 'Partner name';
        break;
    }

    if (event.target.value === 'TaxProfessional') {
      string = 'Freelancer or entity';
    } else if (event.target.value === 'SoftwareProvider') {
      string = 'Product Name';
    }
    setIsLabelName(string);
  };

  return (
    <AppLayout
      // LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="Grow Your Business with TaxZerone Partnership | TaxZerone®"
          description="Partner with TaxZerone for simple, secure, and affordable tax filing solutions. Enhance client satisfaction and boost your business growth today."
          keywords=""
          canonical="grow-your-business/"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="2183"
          ogShareImg=""
        />
      }
    >
      {/* <Popup1098E /> */}
      <Loader isLoading={isLoading} />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        sx={{ top: '150px !important' }}
      >
        <Alert
          onClose={handleSnackClose}
          variant="filled"
          severity="success"
          sx={{
            width: '100%',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          Thanks for contacting us! We will be in touch with you shortly.
        </Alert>
      </Snackbar>
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Box sx={{ backgroundColor: '#fff' }}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{ px: { xs: 2, md: 0 } }}
          >
            <Box
              sx={{
                textAlign: { xs: 'center', md: 'left' },
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                maxWidth: { xs: 'inherit', lg: 770 },
                minWidth: { xs: 'inherit', md: 530 },
                pl: { xs: 0, md: 6, lg: 8, xl: 12 },
                justifyContent: 'center',
              }}
            >
              <Box
                component="div"
                sx={{
                  mt: { xs: 2, sm: 4, md: 5 },
                  mb: { xs: 2, sm: 4, md: 5 },
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                    fontWeight: 600,
                    color: '#374151',
                    mb: 3,
                  }}
                >
                  <span className="highlight-container">
                    <span className="highlight">Grow </span>
                  </span>{' '}
                  Your{' '}
                  <span className="highlight-container">
                    <span className="highlight"> Business</span>
                  </span>
                  <Hidden mdDown>
                    {' '}
                    <br />
                  </Hidden>{' '}
                  with TaxZerone
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                  }}
                >
                  Partner with TaxZerone for simple, secure, and affordable tax
                  filing solutions. Enhance your clients’ experience while
                  growing your business with us.
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                  sx={{ mt: 4 }}
                >
                  <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Join us today!
                    </Typography>
                    <Button
                      component="a"
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        zIndex: 99,
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.1)',
                        },
                      }}
                      href="#contact"
                    >
                      Contact Us{' '}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Box>
            <Hidden mdDown>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                  width: '100%',
                }}
              >
                <picture style={{ marginBottom: -7 }}>
                  <source srcSet="/assets/images/business-landing-img.webp" />
                  <source srcSet="/assets/images/business-landing-img.png" />
                  <img
                    src="/assets/images/business-landing-img.png"
                    alt="Grow Your Business with TaxZerone"
                    className="img-responsive-banner"
                    width={880}
                    height={725}
                  />
                </picture>
              </Box>
            </Hidden>
            <Hidden mdUp smDown>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  width: '100%',
                  backgroundPosition: 'center center !important',
                  mt: '0 !important',
                }}
              >
                <picture style={{ marginBottom: -7 }}>
                  <source srcSet="/assets/images/business-landing-img.webp" />
                  <source srcSet="/assets/images/business-landing-img.png" />
                  <img
                    src="/assets/images/business-landing-img.png"
                    alt="Grow Your Business with TaxZerone"
                    className="img-responsive-banner"
                    width={440}
                    height={365}
                  />
                </picture>
              </Box>
            </Hidden>
          </Stack>
        </Container>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
          <Grid
            container
            spacing={2}
            sx={{ mt: 3, mb: 6, justifyContent: 'center' }}
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Partner with TaxZerone: Unlock Limitless Opportunities
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                Join forces with TaxZerone, an IRS-authorized e-filing platform
                that simplifies business tax filing{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                for organizations across various industries. As an affiliate
                partner, you can unlock significant
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                earning potential while contributing to the mutual growth of our
                businesses. Benefit{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                from our platform&apos;s credibility, user-friendly interface
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { md: 26, sm: 24, xs: 22 },
                  fontWeight: 600,
                  textAlign: 'center',
                  mb: 1,
                }}
              >
                The partnership we offer for Business
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} className="responsive">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 2,
                  textAlign: 'left',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  // display: 'flex',
                  // gap: 2,
                  // alignItems: 'start',
                  backgroundColor: '#fff',
                }}
              >
                <Box sx={{ p: 0 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 2,
                    }}
                  >
                    Tailored Tax Filing Solution
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Partner with us to offer a seamless tax filing service under
                    your brand name. We’ll provide a fully customized software
                    platform featuring your logo and branding, ensuring a
                    cohesive experience for your clients.{' '}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Here’s the best part:{' '}
                  </Typography>

                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.5 } }}>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        We handle everything
                      </span>{' '}
                      — From the tax filing process to customer support, we
                      manage it all behind the scenes.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Earn effortlessly
                      </span>{' '}
                      — Receive a 15% commission on every transaction processed
                      through your platform.
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    This partnership is designed to expand your offerings,
                    enhance your brand’s reputation, and generate a reliable
                    revenue stream without additional operational overhead.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} className="responsive">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 2,
                  textAlign: 'left',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  // display: 'flex',
                  // gap: 2,
                  // alignItems: 'start',
                  backgroundColor: '#fff',
                }}
              >
                <Box sx={{ p: 0 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 2,
                    }}
                  >
                    Referral Program
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Add our product button to your business page and unlock new
                    revenue opportunities. For every client acquired through
                    your referral, you’ll earn a{' '}
                    <span style={{ fontWeight: 600 }}> 15% commission</span> on
                    their transactions.
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Here’s the best part:{' '}
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.5 } }}>
                    <li>
                      <span style={{ fontWeight: 600 }}> Simple setup</span> —
                      Seamlessly integrate our product button with your page.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        Effortless earnings
                      </span>{' '}
                      — We handle all operations, while you enjoy a consistent
                      revenue stream.
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    This is an easy and effective way to expand your services
                    and strengthen your brand with an endless revenue stream.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: 'fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{
                textAlign: { md: 'left', sm: 'center', xs: 'center' },
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
              }}
            >
              <Box>
                <picture>
                  <source srcSet="/assets/images/join-team-img.webp" />
                  <source srcSet="/assets/images/join-team-img.png" />
                  <img
                    src="/assets/images/join-team-img.png"
                    alt=" Join the TaxZerone Partnership Program Today"
                    className="img-responsive"
                    loading="lazy"
                    width={550}
                    height={450}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Join the TaxZerone Partnership Program Today
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Elevate your business offerings and earn more while we handle
                the complexities. Partner with us and start delivering seamless
                tax solutions!
              </Typography>
              <Box sx={{ textAlign: 'left', mr: { xs: 0, md: 4 } }}>
                <Button
                  variant="contained"
                  className="btn-primary"
                  component="a"
                  href="#contact"
                  sx={{
                    zIndex: 99,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  Contact Us{' '}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Benefits of being a partner with TaxZerone
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                We believe in mutual benefits and mutual development. By
                partnering with TaxZerone{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>
                you will experience the unlimited opportunity to earn and
                develop your firm
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/profile-business-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/profile-business-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/profile-business-icon.png"
                      alt="Expand Your Business Portfolio"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Expand Your Business Portfolio
                </Typography>
                <Typography variant="body1">
                  Partner with TaxZerone to diversify your services and include
                  comprehensive tax filing solutions in your offerings. Stand
                  out by catering to your client&apos;s tax needs while
                  enhancing the value of your
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/reliable-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/reliable-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/reliable-icon.png"
                      alt="Deliver a Reliable Platform"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Deliver a Reliable Platform
                </Typography>
                <Typography variant="body1">
                  Earn your clients’ trust with TaxZerone’s dependable and
                  accurate tax filing platform. Provide them with peace of mind
                  through a solution designed for security and compliance.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/form-access-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/form-access-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/form-access-icon.png"
                      alt="Access Extensive Form Coverage"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Access Extensive Form Coverage
                </Typography>
                <Typography variant="body1">
                  With TaxZerone, you can offer over 50 forms across 7
                  categories. From payroll to business taxes, our extensive form
                  coverage ensures your clients have all their filing needs met
                  in one place.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/tax-platform-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/tax-platform-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/tax-platform-icon.png"
                      alt="Simplify Tax Filing "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Simplify Tax Filing
                </Typography>
                <Typography variant="body1">
                  Our platform includes over 30 advanced features to streamline
                  the tax filing process. Experience an intuitive, secure, and
                  efficient solution that makes filing taxes effortless for
                  businesses of all sizes.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/multi-user-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/multi-user-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/multi-user-icon.png"
                      alt="Enjoy Flexible Multi-User Access"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Enjoy Flexible Multi-User Access
                </Typography>
                <Typography variant="body1">
                  Easily manage your team with customizable roles and
                  permissions. Allow one team member to create returns, another
                  to review them, and a third to approve and file—all within the
                  same platform.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/dashboard-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/dashboard-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/dashboard-icon.png"
                      alt="Monitor Through the Partner Portal "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Monitor Through the Partner Portal
                </Typography>
                <Typography variant="body1">
                  Gain full visibility of your client’s activities with our
                  Partner Dashboard. Track progress, monitor filings, and manage
                  your business growth—all in one easy-to-use portal.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          p: 3,
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        id="contact"
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Grid
            container
            spacing={2}
            sx={{ mt: 2, mb: 5, justifyContent: 'center' }}
          >
            <Grid size={12}>
              {' '}
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  lineHeight: 1.4,
                  textAlign: 'center',
                }}
              >
                Transform your client&apos;s and
                <br />
                audience&apos;s tax filing experience.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  fontWeight: 500,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Partner with us today!
              </Typography>
            </Grid>

            <Grid size={12} sx={{ textAlign: 'center' }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  maxWidth: { xs: '100%', sm: 900 },
                  width: '100%',
                  textAlign: 'center',
                  justifyContent: 'center',
                  alignItems: 'center',
                  m: 'auto',
                  borderRadius: 4,
                  border: 'none',
                  boxShadow: '-4px 1px 17px -8px rgba(0,0,0,0.32)',
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 2,
                    mb: 3,
                    lineHeight: 1.5,
                    textAlign: 'center',
                  }}
                >
                  Contact Us
                </Typography> */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Name"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Name"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Name"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="DesignationOfContactPerson"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="DesignationOfContactPerson"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Designation of Contact Person"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                      <InputLabel color="secondary">Who are you?</InputLabel>
                      <Controller
                        name="PartnerType"
                        control={control}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <Select
                              native
                              labelId="PartnerType"
                              name="PartnerType"
                              id="PartnerType"
                              label="Who are you?"
                              color="secondary"
                              error={!!error}
                              className={
                                value !== PartnerType.None ? 'prefilled' : ''
                              }
                              fullWidth
                              value={value?.toString()}
                              onChange={(event) => {
                                onChange(event);
                                handlePartnerType(event);
                              }}
                            >
                              <option
                                key={PartnerType.None}
                                value={PartnerType.None}
                              />
                              <option
                                key={PartnerType.Business}
                                value={PartnerType.Business}
                              >
                                {PartnerType.Business}
                              </option>
                              <option
                                key={PartnerType.TaxProfessional}
                                value="TaxProfessional"
                              >
                                {PartnerType.TaxProfessional}
                              </option>
                              <option
                                key={PartnerType.Influencer}
                                value={PartnerType.Influencer}
                              >
                                {PartnerType.Influencer}
                              </option>
                              <option
                                key={PartnerType.Association}
                                value={PartnerType.Association}
                              >
                                {PartnerType.Association}
                              </option>
                              <option
                                key={PartnerType.SoftwareProvider}
                                value="SoftwareProvider"
                              >
                                {PartnerType.SoftwareProvider}
                              </option>
                            </Select>
                          </>
                        )}
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Phone"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Phone"
                          label="*Phone Number"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          InputProps={{
                            inputComponent: CustomTextMask as any,
                            inputProps: {
                              mask: '(#00) 000-0000',
                              maxLength: 14,
                            },
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    {' '}
                    <Controller
                      name="PartnerName"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="PartnerName"
                          className={value ? 'prefilled' : ''}
                          label={isLabelName}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          inputProps={{
                            maxLength: 150,
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Email"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Email"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Email Address"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Website"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Website"
                          className={value ? 'prefilled' : ''}
                          label="Website (if applicable)"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="NatureOfBusiness"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="NatureOfBusiness"
                          className={value ? 'prefilled' : ''}
                          label="Nature of Business"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      m: 'auto',
                    }}
                  >
                    {' '}
                    <Controller
                      name="Message"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Message"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Description/Comments:"
                          multiline
                          rows={4}
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        mt: 3,
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Partner with Us and Grow!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Join TaxZerone’s Partner Program to boost your business, earn rewards,
          and deliver
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          trusted tax filing services to your clients. Success is better
          together!
        </Typography>

        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href="#contact"
          >
            Let’s Partner Up
          </Button>
        </Box>
      </Box>
      {/* <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component={'div'}
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the TaxZerone Affiliate Program?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      By recommending our reliable tax filing platform to your
                      network, you can make money through the TaxZerone
                      Affiliate Program.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Once you sign up, you’ll receive a unique referral link.
                      It&apos;s a simple way to earn income from your
                      network—each time someone successfully files their taxes
                      using your link, you get paid.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How can I monitor my earnings and referrals?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      You&apos;ll have access to an affiliate dashboard once you
                      sign up. Clicks, sign-ups, and completed purchases are all
                      included in the comprehensive reports it displays. From
                      your dashboard, you can effortlessly monitor your
                      commissions and referral performance in real-time.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Are there any fees to join the affiliate program?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 0 }}>
                      <span style={{ fontWeight: 600 }}> No</span>, there is no
                      cost to sign up for the TaxZerone Affiliate Program. No
                      hidden fees, subscription fees, or sign-up costs apply.
                      With no initial outlay of funds or commitments, you can
                      begin promoting TaxZerone and receiving commissions.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Can I join if I live in a different country?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography component={'div'} sx={{ mt: 1, mb: 0 }}>
                      Yes, the program is open to anyone in the world. However,
                      your referrals should mostly be U.S. taxpayers because
                      TaxZerone manages U.S. tax filings. In this manner, you
                      can successfully market the service to a group of people
                      who will use it.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. Can I use email marketing to promote TaxZerone?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography component={'div'} sx={{ mt: 1, mb: 0 }}>
                      <span style={{ fontWeight: 600 }}> Yes</span>, you can use
                      email campaigns to advertise TaxZerone. To ensure that
                      your outreach is both lawful and considerate, please make
                      sure you abide by applicable email laws, such as CAN-SPAM,
                      and send emails only to contacts who have consented to
                      receive your communications.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  6. How do I get in touch with affiliate support?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component={'div'} itemProp="text">
                    <Typography component={'div'} sx={{ mt: 1, mb: 0 }}>
                      Our committed affiliate support staff is available to help
                      if you need it. You can reach us by email at
                      <Link href="/contact-us/" sx={{ color: '#0202ff' }}>
                        {' '}
                        support@taxzerone.com
                      </Link>{' '}
                      or by using the support link in your affiliate dashboard.
                      We are available to help you succeed and respond to your
                      inquiries.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container> */}
    </AppLayout>
  );
};

export default Partner;
